/* eslint-disable import/prefer-default-export */
import { createActions, createAction } from 'redux-actions';
import queryString from 'query-string';
import snakeCaseKeys from 'snakecase-keys';
import { stateDataLayer } from 'shared/modules';
import { setDataLayer } from 'shared/actions/globals';
import { SEARCH_FORM } from 'shared/constants/actionTypes';
import { setVocabularyField } from 'shared/actions/partsForm';
import customs from 'shared/constants/json/customs.json';
import { generateSearchQuery } from 'shared/helpers/urls';
import { getSearchQuery } from 'shared/helpers/search';
import currencies from 'shared/constants/json/currency.json';

import { ANY_TYPE_OMITTED_FIELDS, OMMITED_FIELDS_BY_TRANSPORT_ID } from 'shared/constants/adverts';
import { normalizePlainOptionIDsToSelected } from '../helpers/advertForm';
import API from '../constants/api';
import { getRange } from '../helpers/arrays';

const sluggifiedCurrency = currencies.map(item => ({ ...item, id: item.slug }));

//
// Selected
//

// exporting selectedActions
const actions = createActions(
  {
    SEARCH_FORM: {
      SELECTED: Object.keys(SEARCH_FORM.SELECTED).reduce(
        (object, field) => ({ ...object, [field]: undefined }),
        {},
      ),
    },
  },
  { namespace: '__' },
);
// exporting selectedActions like this due to an extract-hoc package bug
export const selectedActions = actions.searchForm.selected;

//
// Data
//

export const setData = {
  models: createAction(SEARCH_FORM.DATA_MODELS_SET),
  brandToChose: createAction(SEARCH_FORM.DATA_BRAND_TO_CHOSE_SET),
  modifications: createAction(SEARCH_FORM.DATA_MODIFICATIONS_SET),
  equipments: createAction(SEARCH_FORM.DATA_EQUIPMENTS_SET),
  prices: createAction(SEARCH_FORM.DATA_PRICES_SET),
  years: createAction(SEARCH_FORM.DATA_YEARS_SET),
  generations: createAction(SEARCH_FORM.DATA_GENERATIONS_SET),
  series: createAction(SEARCH_FORM.DATA_SERIES_SET),
  count: createAction(SEARCH_FORM.DATA_COUNT_SET),
  makesCounter: createAction(SEARCH_FORM.DATA_MAKES_COUNTER_SET),
  sparetypes: createAction(SEARCH_FORM.DATA_SPARETYPES_SET),
};

//
// Clear
//

export const clearSelected = createAction(SEARCH_FORM.CLEAR);
export const resetAll = createAction(SEARCH_FORM.RESET_ALL);

//
// Results
//

export const setResults = createAction(SEARCH_FORM.RESULTS_SET);

//
// Results
//

export const setCounts = createAction(SEARCH_FORM.COUNTS_SET);

//
// Vocabulary
//

export const setFormVocabulary = createAction(SEARCH_FORM.VOCABULARY_SET);

// Helpers for thunks

const findItemInCollection = (collection, item, key = 'id') =>
  collection ? collection.find(i => i[key] === item) : item;

const fetchModels = (fetch, dispatch, make, transport) =>
  fetch(API.CMM.modelsByMake(make.id, transport && transport.id))
    .then(r => r.json())
    .then(models => {
      dispatch(setData.models(models));
    });

//
// Thunks
//

const { set, toggle, clear } = selectedActions;

export const resultsThunks = {
  searchResults: queries => (dispatch, getState, { fetch }) => {
    const missingQueriesKeys = Object.keys(queries);
    const setDataLayerArr = [];
    return Promise.all(
      missingQueriesKeys.map(key =>
        fetch(
          queries[key].url
            ? queries[key].url
            : `${API.ADVERT.LIST}?${queries[key]}&omit=${[
                ...ANY_TYPE_OMITTED_FIELDS,
                ...(OMMITED_FIELDS_BY_TRANSPORT_ID[queryString.parse(queries[key]).transport] ||
                  []),
              ]}`,
        )
          .then(r => r.json())
          .then(response => {
            const result = {};
            result[key] = response;

            if (Array.isArray(response.results)) {
              response.results.map(a => {
                setDataLayerArr.push(a);
                return '';
              });
            }
            dispatch(
              setDataLayer(
                stateDataLayer.manipulation({ data: setDataLayerArr, pagetype: 'searchresults' }),
              ),
            );
            // dispatch(setResults(result));
            return result;
          }),
      ),
    );
  },
  searchDealersResults: query => (dispatch, getState, { fetch }) =>
    fetch(API.DEALERS.ALL(`?${query}`)).then(r => r.json()).then(response => {
      const result = {};
      result[`dealers-AJAX`] = response;
      dispatch(setResults(result));
      Promise.resolve({});
    }),

  searchFirmsResults: (dataQuery = {}, params) => (dispatch, getState, { fetch }) => {
    const { partsForm: { vocabulary: { query = '', queryobj } } = {} } = getState();
    if (params && params.name === 'topFirms') {
      const { topMakesFilter = true } = params;
      const { services = '', makes = '' } = queryobj;
      delete queryobj.services;
      delete queryobj.makes;

      if (services) queryobj.top_services = services;
      if (makes && topMakesFilter) queryobj.top_makes = makes;
    }
    const queryRequest =
      queryString.stringify({
        ...dataQuery,
        ...queryobj,
      }) || query;

    return fetch(API.FIRMS.ALL(`?${queryRequest}`))
      .then(r => (r.ok ? r.json() : null))
      .then(response => {
        const result = {};
        result[(params && params.name) || 'firms'] = response;
        if (!params) {
          dispatch(setVocabularyField.count(response.count));
        }
        if (params && params.count) {
          const { partsForm: { vocabulary: { count = 0 } } = {} } = getState();
          dispatch(setVocabularyField.count(Number(response.length) + Number(count)));
        }
        dispatch(setResults(result));
        Promise.resolve(response);
      });
  },
  searchSparesResults: query => (dispatch, getState, { fetch }) => {
    const results = getState().searchForm.results || {};
    const fieldKey = `spares-${query}`;

    if (results[fieldKey]) {
      return Promise.resolve({});
    }

    return fetch(API.SPARE_PARTS.ALL(`?${query}`)).then(r => r.json()).then(response => {
      dispatch(setResults({ [fieldKey]: response }));
      dispatch(
        setDataLayer(
          stateDataLayer.manipulation({ data: response.results, pagetype: 'searchresults' }),
        ),
      );
      Promise.resolve({});
    });
  },
};

export const transportSetThunks = {
  currency: currency => dispatch => {
    dispatch(set.currency(currency));
    dispatch(transportSetThunks.count());
  },

  monthly: payment => dispatch => {
    dispatch(set.monthly(payment));
    dispatch(transportSetThunks.count());
  },

  prepaid: prepaid => dispatch => {
    dispatch(set.prepaid(prepaid));
    if (!prepaid) {
      dispatch(set.make(null));
      dispatch(set.model(null));
      dispatch(set.monthly(null));
      // dispatch(setData.models(null));
    }
    dispatch(transportSetThunks.count());
  },

  yearTo: yearTo => dispatch => {
    dispatch(set.yearTo(yearTo));
    dispatch(transportSetThunks.count());
  },

  yearFrom: yearFrom => dispatch => {
    dispatch(set.yearFrom(yearFrom));
    dispatch(transportSetThunks.count());
  },

  fromDealer: fromDealer => dispatch => {
    dispatch(set.fromDealer(fromDealer));
    dispatch(transportSetThunks.count());
  },

  priceFrom: priceFrom => dispatch => {
    dispatch(set.priceFrom(priceFrom));
    dispatch(transportSetThunks.count());
  },

  priceTo: priceTo => dispatch => {
    dispatch(set.priceTo(priceTo));
    dispatch(transportSetThunks.count());
  },

  powerFrom: powerFrom => dispatch => {
    dispatch(set.powerFrom(powerFrom));
    dispatch(transportSetThunks.count());
  },

  powerTo: powerTo => dispatch => {
    dispatch(set.powerTo(powerTo));
    dispatch(transportSetThunks.count());
  },

  capacityFrom: capacityFrom => dispatch => {
    dispatch(set.capacityFrom(capacityFrom));
    dispatch(transportSetThunks.count());
  },

  capacityTo: capacityTo => dispatch => {
    dispatch(set.capacityTo(capacityTo));
    dispatch(transportSetThunks.count());
  },

  mileageFrom: mileageFrom => dispatch => {
    dispatch(set.mileageFrom(mileageFrom));
    dispatch(transportSetThunks.count());
  },
  company: mileageFrom => dispatch => {
    dispatch(set.company(mileageFrom));
    dispatch(transportSetThunks.count());
  },
  typeWork: mileageFrom => dispatch => {
    dispatch(set.typeWork(mileageFrom));
    dispatch(transportSetThunks.count());
  },
  mileageTo: mileageTo => dispatch => {
    dispatch(set.mileageTo(mileageTo));
    dispatch(transportSetThunks.count());
  },

  engine: engine => dispatch => {
    dispatch(set.engine(engine));
    dispatch(transportSetThunks.count());
  },

  drive: drive => dispatch => {
    dispatch(set.drive(drive));
    dispatch(transportSetThunks.count());
  },

  gearbox: gearbox => dispatch => {
    dispatch(set.gearbox(gearbox));
    dispatch(transportSetThunks.count());
  },

  category: category => dispatch => {
    dispatch(set.category(category));
    dispatch(transportSetThunks.count());
  },

  isCrashed: isCrashed => dispatch => {
    dispatch(set.isCrashed(isCrashed));
    dispatch(transportSetThunks.count());
  },

  isNotCleared: isNotCleared => dispatch => {
    dispatch(set.isNotCleared(isNotCleared));
    dispatch(transportSetThunks.count());
  },

  isSaleOnCredit: isSaleOnCredit => dispatch => {
    dispatch(set.isSaleOnCredit(isSaleOnCredit));
    dispatch(transportSetThunks.count());
  },

  isUnderLoan: isUnderLoan => dispatch => {
    dispatch(set.isUnderLoan(isUnderLoan));
    dispatch(transportSetThunks.count());
  },

  location: location => dispatch => {
    dispatch(set.location(location));
    dispatch(transportSetThunks.count());
  },

  isSwap: isSwap => dispatch => {
    dispatch(set.isSwap(isSwap));
    dispatch(transportSetThunks.count());
  },

  isRent: isRent => dispatch => {
    dispatch(set.isRent(isRent));
    dispatch(transportSetThunks.count());
  },

  isNew: isNew => dispatch => {
    dispatch(set.isNew(isNaN(isNew) ? null : isNew));
    dispatch(transportSetThunks.count());
  },

  photos: photos => dispatch => {
    dispatch(set.photos(photos));
    dispatch(transportSetThunks.count());
  },

  fromAuction: fromAuction => dispatch => {
    dispatch(set.fromAuction(fromAuction));
    dispatch(transportSetThunks.count());
  },

  colors: colors => dispatch => {
    dispatch(toggle.colors(colors));
    dispatch(transportSetThunks.count());
  },

  clearSelected: () => dispatch => {
    dispatch(clearSelected());
    dispatch(transportSetThunks.count());
  },

  abroad: abroad => dispatch => {
    dispatch(set.abroad(abroad));

    if (abroad && +abroad.value) {
      dispatch(set.drivenFrom(null));
      dispatch(set.isNotCleared(customs[1]));
    }

    dispatch(transportSetThunks.count());
  },

  drivenFrom: drivenFrom => dispatch => {
    dispatch(set.drivenFrom(drivenFrom));
    dispatch(transportSetThunks.count());
  },

  count: transferredState => async (dispatch, getState, { fetch }) => {
    const { searchForm: { selected: formState }, globals } = getState();
    const location = (globals && globals.location) || null;

    const searchQuery = {
      ...(transferredState || formState),
      location: formState.dealer ? null : location,
    };

    // Remove location if it is county and we already set `drivenFrom` field.
    if (searchQuery.location && searchQuery.location.is_country && searchQuery.drivenFrom) {
      searchQuery.location = null;
    }

    // const query = snakeCaseKeys(generateSearchQuery(searchQuery));
    const query = getSearchQuery(searchQuery);
    const key = queryString.stringify(query);
    try {
      await fetch(`${API.ADVERT.COUNT}?${key}`).then(r => r.json()).then(response => {
        dispatch(setData.count(response.count));
      });
    } catch (e) {
      console.error(e);
    }
  },
};

export const carSetThunks = {
  /* CMM */

  ...transportSetThunks,

  make: make => (dispatch, getState, { fetch }) => {
    dispatch(set.make(make));
    dispatch(set.model(null));
    dispatch(setData.models(null));
    dispatch(setData.generations(null));
    dispatch(set.generation(null));
    dispatch(carSetThunks.count());

    if (make && (make.id || make) > 0) {
      fetchModels(fetch, dispatch, make);
    }
  },

  model: model => (dispatch, getState, { fetch }) => {
    dispatch(set.model(model));
    dispatch(setData.generations(null));
    dispatch(set.generation(null));
    dispatch(carSetThunks.count());

    if (model) {
      fetch(API.CMM.generationsByModel(model.id)).then(r => r.json()).then(generations => {
        dispatch(setData.generations(generations));
      });
    }
  },

  generation: generation => dispatch => {
    dispatch(set.generation(generation));
    dispatch(carSetThunks.count());
  },

  // Other

  setSelectedOptions: pickedOptions => (dispatch, getState) => {
    dispatch(clear.options());

    const optionsTree = getState().searchForm.vocabulary.options;
    const normalizedOptions = normalizePlainOptionIDsToSelected(pickedOptions, optionsTree);

    dispatch(set.options(normalizedOptions));
  },

  usedWarranty: usedWarranty => dispatch => {
    dispatch(set.usedWarranty(usedWarranty));
  },
};

export const transportFillThunks = {
  currency: currency => dispatch => {
    const item = findItemInCollection(sluggifiedCurrency, currency, 'slug');
    dispatch(set.currency(item));
  },

  monthly: payment => dispatch => {
    dispatch(set.monthly(payment));
  },

  prepaid: prepaid => dispatch => {
    if (!prepaid) dispatch(set.monthly(null));
    dispatch(set.prepaid(prepaid));
  },

  engine: engineId => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.engines;
    const item = findItemInCollection(collection, parseInt(engineId, 10));
    dispatch(set.engine(item));
  },

  drive: driveId => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.drives;
    const item = findItemInCollection(collection, parseInt(driveId, 10));
    dispatch(set.drive(item));
  },

  gearbox: gearboxId => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.gearboxes;
    const gearbox = findItemInCollection(collection, parseInt(gearboxId, 10));
    dispatch(set.gearbox(gearbox));
  },

  category: categoryId => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.categories;
    const category = findItemInCollection(collection, parseFloat(categoryId));
    dispatch(set.category(category));
  },

  yearTo: yearTo => dispatch => {
    dispatch(set.yearTo(yearTo));
  },

  yearFrom: yearFrom => dispatch => {
    dispatch(set.yearFrom(yearFrom));
  },

  priceFrom: priceFrom => dispatch => {
    dispatch(set.priceFrom(priceFrom));
  },

  fromDealer: fromDealer => dispatch => {
    dispatch(set.fromDealer(fromDealer));
  },

  priceTo: priceTo => dispatch => {
    dispatch(set.priceTo(priceTo));
  },

  powerFrom: powerFrom => dispatch => {
    dispatch(set.powerFrom(powerFrom));
  },

  powerTo: powerTo => dispatch => {
    dispatch(set.powerTo(powerTo));
  },

  capacityFrom: capacityFromVal => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.capacities;
    const capacityFrom = findItemInCollection(collection, parseFloat(capacityFromVal), 'value');
    dispatch(set.capacityFrom(capacityFrom));
  },

  capacityTo: capacityFromVal => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.capacities;
    const capacityTo = findItemInCollection(collection, parseFloat(capacityFromVal), 'value');
    dispatch(set.capacityTo(capacityTo));
  },

  mileageFrom: mileageFrom => dispatch => {
    dispatch(set.mileageFrom(mileageFrom));
  },

  mileageTo: mileageTo => dispatch => {
    dispatch(set.mileageTo(mileageTo));
  },

  colors: colors => dispatch => {
    dispatch(set.colors(colors));
  },

  ordering: ordering => dispatch => {
    dispatch(set.ordering(ordering));
  },

  period: period => dispatch => {
    dispatch(set.period(period));
  },

  currecny: currecny => dispatch => {
    dispatch(set.currecny(currecny));
  },

  photos: photos => dispatch => {
    dispatch(set.photos(photos));
  },

  fromAuction: fromAuction => dispatch => {
    dispatch(set.fromAuction(fromAuction));
  },

  isSwap: isSwap => dispatch => {
    dispatch(set.isSwap(isSwap));
  },

  dealer: dealer => dispatch => {
    dispatch(set.dealer(dealer));
  },

  isUnderLoan: isUnderLoan => dispatch => {
    dispatch(set.isUnderLoan(isUnderLoan));
  },

  isCrashed: isCrashed => (dispatch, getState) => {
    const isNew = getState().searchForm.selected.isNew;
    if (isNew) {
      dispatch(set.isCrashed(null));
    } else {
      const collection = getState().searchForm.vocabulary.states;
      const crashedStatus = findItemInCollection(
        collection,
        isCrashed ? parseInt(isCrashed, 0) : isCrashed,
      );
      dispatch(set.isCrashed(crashedStatus));
    }
  },

  isNotCleared: isNotCleared => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.customs;
    const customsStatus = findItemInCollection(
      collection,
      isNotCleared ? parseInt(isNotCleared, 0) : isNotCleared,
    );
    dispatch(set.isNotCleared(customsStatus));
  },

  abroad: abroad => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.abroadOptions;
    const customsStatus = findItemInCollection(collection, abroad ? parseInt(abroad, 0) : abroad);
    dispatch(set.abroad(customsStatus));
  },

  drivenFrom: drivenFrom => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.europeanCountries;
    const customsStatus = findItemInCollection(
      collection,
      drivenFrom ? parseInt(drivenFrom, 0) : drivenFrom,
    );
    dispatch(set.drivenFrom(customsStatus));
  },

  isRent: isRent => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.rent;
    const status = findItemInCollection(collection, isRent ? parseInt(isRent, 0) : isRent);
    dispatch(set.isRent(status));
  },

  isSaleOnCredit: isSaleOnCredit => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.credits;
    const status = findItemInCollection(
      collection,
      isSaleOnCredit ? parseInt(isSaleOnCredit, 0) : isSaleOnCredit,
    );
    dispatch(set.isSaleOnCredit(status));
  },

  isNew: type => (dispatch, getState) => {
    const usedWarranty = getState().searchForm.selected.usedWarranty;
    if (usedWarranty) dispatch(set.usedWarranty(null));

    if (type) dispatch(set.isCrashed(null));
    dispatch(set.isNew(type === null ? null : parseInt(type, 10)));
  },
};

export const dataThunks = {
  brandToChose: make => dispatch => {
    dispatch(setData.brandToChose(make));
    dispatch(carSetThunks.make(make));
    if (!make) dispatch(clearSelected());
  },
};

// Fill form from url

export const carFormFillThunks = {
  make: makeId => (dispatch, getState, { fetch }) => {
    const collection = getState().searchForm.vocabulary.makes;
    const make = findItemInCollection(collection, parseInt(makeId, 10));
    dispatch(set.make(make));
    if (make !== undefined && (make.id || make) > 0) {
      fetchModels(fetch, dispatch, make);
    }
  },

  model: modelId => (dispatch, getState, { fetch }) => {
    fetch(API.CMM.modelById(modelId)).then(r => r.json()).then(response => {
      if (response) {
        dispatch(set.model(response));
      }
    });
  },

  generation: generationId => (dispatch, getState, { fetch }) => {
    fetch(API.CMM.generationById(generationId)).then(r => r.json()).then(response => {
      if (response) {
        dispatch(set.generation(response));
      }
    });
  },
  ...transportFillThunks,
};

export const motoSetThunks = {
  ...transportSetThunks,

  make: make => (dispatch, getState, { fetch }) => {
    dispatch(set.make(make));
    dispatch(set.model(null));
    dispatch(setData.models(null));
    dispatch(motoSetThunks.count());

    const transport = getState().searchForm.selected.transport || {};

    if (make) {
      fetchModels(fetch, dispatch, make, transport);
    }
  },

  model: model => dispatch => {
    dispatch(set.model(model));
    dispatch(motoSetThunks.count());
  },

  transportChild: transportChild => dispatch => {
    dispatch(set.transportChild(transportChild));
    dispatch(motoSetThunks.count());
  },

  tacts: tacts => dispatch => {
    dispatch(set.tacts(tacts));
    dispatch(motoSetThunks.count());
  },

  engineOrder: engineOrder => dispatch => {
    dispatch(set.engineOrder(engineOrder));
    dispatch(motoSetThunks.count());
  },
};

export const motoFormFillThunks = {
  make: makeId => (dispatch, getState, { fetch }) => {
    const collection = getState().searchForm.vocabulary.makes;
    const make = findItemInCollection(collection, parseFloat(makeId));
    dispatch(set.make(make));

    if (make) {
      fetchModels(fetch, dispatch, make);
    }
  },

  model: modelId => (dispatch, getState, { fetch }) => {
    fetch(API.CMM.modelById(modelId)).then(r => r.json()).then(response => {
      if (response) {
        dispatch(set.model(response));
      }
    });
  },

  transportChild: childId => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.types;
    const transportChild = findItemInCollection(collection, parseFloat(childId));
    dispatch(set.transportChild(transportChild));
  },

  tacts: tactsId => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.tacts;
    const tacts = findItemInCollection(collection, parseFloat(tactsId));
    dispatch(set.tacts(tacts));
  },

  engineOrder: engineOrderId => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.engineOrders;
    const engineOrder = findItemInCollection(collection, parseFloat(engineOrderId));
    dispatch(set.engineOrder(engineOrder));
  },

  ...transportFillThunks,
};

export const commercialSetThunks = {
  ...transportSetThunks,

  make: make => (dispatch, getState, { fetch }) => {
    dispatch(set.make(make));
    dispatch(commercialSetThunks.model(null));
    dispatch(setData.models(null));
    dispatch(commercialSetThunks.count());

    const transport = getState().searchForm.selected.transport || {};

    if (make) {
      fetchModels(fetch, dispatch, make, transport);
    }
  },

  model: model => dispatch => {
    dispatch(set.model(model));
    dispatch(commercialSetThunks.count());
  },

  transportChild: transportChild => dispatch => {
    dispatch(set.transportChild(transportChild));
    dispatch(commercialSetThunks.count());
  },

  ecotype: ecotype => dispatch => {
    dispatch(set.ecotype(ecotype));
    dispatch(commercialSetThunks.count());
  },

  unloading: unloading => dispatch => {
    dispatch(set.unloading(unloading));
    dispatch(commercialSetThunks.count());
  },

  tonnageFrom: tonnageFrom => dispatch => {
    dispatch(set.tonnageFrom(tonnageFrom));
    dispatch(commercialSetThunks.count());
  },

  tonnageTo: tonnageTo => dispatch => {
    dispatch(set.tonnageTo(tonnageTo));
    dispatch(commercialSetThunks.count());
  },

  capacityBodyFrom: capacityBodyFrom => dispatch => {
    dispatch(set.capacityBodyFrom(capacityBodyFrom));
    dispatch(commercialSetThunks.count());
  },

  capacityBodyTo: capacityBodyTo => dispatch => {
    dispatch(set.capacityBodyTo(capacityBodyTo));
    dispatch(commercialSetThunks.count());
  },

  capacityBucketFrom: capacityBucketFrom => dispatch => {
    dispatch(set.capacityBucketFrom(capacityBucketFrom));
    dispatch(commercialSetThunks.count());
  },

  capacityBucketTo: capacityBucketTo => dispatch => {
    dispatch(set.capacityBucketTo(capacityBucketTo));
    dispatch(commercialSetThunks.count());
  },

  layingWidthFrom: layingWidthFrom => dispatch => {
    dispatch(set.layingWidthFrom(layingWidthFrom));
    dispatch(commercialSetThunks.count());
  },

  layingWidthTo: layingWidthTo => dispatch => {
    dispatch(set.layingWidthTo(layingWidthTo));
    dispatch(commercialSetThunks.count());
  },

  liftHeightFrom: liftHeightFrom => dispatch => {
    dispatch(set.liftHeightFrom(liftHeightFrom));
    dispatch(commercialSetThunks.count());
  },

  liftHeightTo: value => dispatch => {
    dispatch(set.liftHeightTo(value));
    dispatch(commercialSetThunks.count());
  },

  admissionHeightFrom: value => dispatch => {
    dispatch(set.admissionHeightFrom(value));
    dispatch(commercialSetThunks.count());
  },

  admissionHeightTo: value => dispatch => {
    dispatch(set.admissionHeightTo(value));
    dispatch(commercialSetThunks.count());
  },

  armBoomFrom: value => dispatch => {
    dispatch(set.armBoomFrom(value));
    dispatch(commercialSetThunks.count());
  },

  armBoomTo: value => dispatch => {
    dispatch(set.armBoomTo(value));
    dispatch(commercialSetThunks.count());
  },

  depBigFrom: value => dispatch => {
    dispatch(set.depBigFrom(value));
    dispatch(commercialSetThunks.count());
  },

  depBigTo: value => dispatch => {
    dispatch(set.depBigTo(value));
    dispatch(commercialSetThunks.count());
  },

  productivityFrom: value => dispatch => {
    dispatch(set.productivityFrom(value));
    dispatch(commercialSetThunks.count());
  },

  productivityTo: value => dispatch => {
    dispatch(set.productivityTo(value));
    dispatch(commercialSetThunks.count());
  },
};

export const commercialFormFillThunks = {
  make: makeId => (dispatch, getState, { fetch }) => {
    const collection = getState().searchForm.vocabulary.makes;
    const make = findItemInCollection(collection, parseFloat(makeId));
    dispatch(set.make(make));

    if (make) {
      fetchModels(fetch, dispatch, make);
    }
  },

  model: modelId => (dispatch, getState, { fetch }) => {
    fetch(API.CMM.modelById(modelId)).then(r => r.json()).then(response => {
      if (response) {
        dispatch(set.model(response));
      }
    });
  },

  transportChild: childId => (dispatch, getState) => {
    const transport = getState().searchForm.selected.transport || {};
    const collection = transport
      ? getState().searchForm.vocabulary.childrenTransportTypes[transport.slug]
      : [];
    const transportChild = findItemInCollection(collection, parseFloat(childId));
    dispatch(set.transportChild(transportChild));
  },

  ...transportFillThunks,

  ecotype: ecotypeId => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.ecotypes;
    const ecotype = findItemInCollection(collection, parseFloat(ecotypeId));
    dispatch(set.ecotype(ecotype));
  },

  unloading: unloadingId => (dispatch, getState) => {
    const collection = getState().searchForm.vocabulary.unloadings;
    const unloading = findItemInCollection(collection, parseFloat(unloadingId));
    dispatch(set.unloading(unloading));
  },

  tonnageFrom: tonnageFrom => dispatch => {
    dispatch(set.tonnageFrom(tonnageFrom));
  },

  tonnageTo: tonnageTo => dispatch => {
    dispatch(set.tonnageTo(tonnageTo));
  },

  capacityBodyFrom: value => dispatch => {
    dispatch(set.capacityBodyFrom(value));
  },

  capacityBodyTo: value => dispatch => {
    dispatch(set.capacityBodyTo(value));
  },

  capacityBucketFrom: value => dispatch => {
    dispatch(set.capacityBucketFrom(value));
  },

  capacityBucketTo: value => dispatch => {
    dispatch(set.capacityBucketTo(value));
  },

  layingWidthFrom: value => dispatch => {
    dispatch(set.layingWidthFrom(value));
  },

  layingWidthTo: value => dispatch => {
    dispatch(set.layingWidthTo(value));
  },

  liftHeightFrom: value => dispatch => {
    dispatch(set.liftHeightFrom(value));
  },

  liftHeightTo: value => dispatch => {
    dispatch(set.liftHeightTo(value));
  },

  admissionHeightFrom: value => dispatch => {
    dispatch(set.admissionHeightFrom(value));
  },

  admissionHeightTo: value => dispatch => {
    dispatch(set.admissionHeightTo(value));
  },

  armBoomFrom: value => dispatch => {
    dispatch(set.armBoomFrom(value));
  },

  armBoomTo: value => dispatch => {
    dispatch(set.armBoomTo(value));
  },

  depBigFrom: value => dispatch => {
    dispatch(set.depBigFrom(value));
  },

  depBigTo: value => dispatch => {
    dispatch(set.depBigTo(value));
  },

  productivityFrom: value => dispatch => {
    dispatch(set.productivityFrom(value));
  },

  productivityTo: value => dispatch => {
    dispatch(set.productivityTo(value));
  },
};

//
// Dealer Cars
//

export const dealerCarsThunks = {
  make: make => (dispatch, getState) => {
    dispatch(set.make(make));
    dispatch(set.model(null));
    dispatch(setData.models(null));
    dispatch(set.count());

    if (make && make.id) {
      const dealer = getState().searchForm.selected.dealer;
      const models = dealer ? dealer.models.filter(model => model.make === make.id) : [];
      dispatch(setData.models(models));
    }
  },
};

//
// Dealers section
//

export const dealersSetThunks = {
  hasNew: isNew => dispatch => {
    dispatch(set.hasNew(isNew));
    dispatch(dealersSetThunks.count());
  },

  make: make => (dispatch, getState, { fetch }) => {
    dispatch(set.make(make));
    dispatch(set.model(null));
    dispatch(setData.models(null));
    dispatch(dealersSetThunks.count());

    const transport = getState().searchForm.selected.transport || {};

    if (make) {
      fetchModels(fetch, dispatch, make, transport);
    }
  },

  model: model => (dispatch, getState, { fetch }) => {
    dispatch(set.model(model));
    dispatch(setData.generations(null));
    dispatch(dealersSetThunks.count());

    if (model) {
      fetch(API.CMM.generationsByModel(model.id)).then(r => r.json()).then(generations => {
        dispatch(setData.generations(generations));
      });
    }
  },

  count: transferredState => (dispatch, getState, { fetch }) => {
    const { searchForm: { selected: formState }, globals } = getState();
    const location = (globals && globals.location) || null;

    const searchQuery = {
      ...(transferredState || formState),
      location: formState.dealer ? null : location,
    };
    const { currency, capacityUnit, powerUnit, isNew, ...usefulParams } = searchQuery;
    const query = snakeCaseKeys(generateSearchQuery(usefulParams));
    const key = queryString.stringify(query);
    fetch(`${API.DEALERS.COUNT}?${key}`).then(r => r.json()).then(response => {
      dispatch(setData.count(response.count));
    });
  },

  powerUnit: powerUnit => dispatch => {
    dispatch(set.powerUnit(powerUnit));
  },

  delaer: delaer => dispatch => {
    dispatch(set.powerUnit(delaer));
  },

  tags: tags => dispatch => {
    dispatch(set.tags(tags));
  },

  capacityUnit: capacityUnit => dispatch => {
    dispatch(set.capacityUnit(capacityUnit));
  },

  isOfficial: isOfficial => dispatch => {
    dispatch(set.isOfficial(isOfficial));
    dispatch(dealersSetThunks.count());
  },

  q: q => dispatch => {
    dispatch(set.q(q));
    dispatch(dealersSetThunks.count());
  },

  testDrive: testDrive => dispatch => {
    dispatch(set.testDrive(testDrive));
    dispatch(dealersSetThunks.count());
  },

  tradeIn: tradeIn => dispatch => {
    dispatch(set.tradeIn(tradeIn));
    dispatch(dealersSetThunks.count());
  },

  credit: credit => dispatch => {
    dispatch(set.credit(credit));
    dispatch(dealersSetThunks.count());
  },

  insurance: insurance => dispatch => {
    dispatch(set.insurance(insurance));
    dispatch(dealersSetThunks.count());
  },

  accessories: accessories => dispatch => {
    dispatch(set.accessories(accessories));
    dispatch(dealersSetThunks.count());
  },

  clearSelected: () => dispatch => {
    dispatch(clearSelected());
    dispatch(dealersSetThunks.count());
  },
};

export const dealersFormFillThunks = {
  hasNew: type => dispatch => {
    dispatch(set.hasNew(parseInt(type, 10)));
  },

  make: makeId => (dispatch, getState, { fetch }) => {
    const collection = getState().searchForm.vocabulary.makes;
    const make = findItemInCollection(collection, parseInt(makeId, 10));

    dispatch(set.make(make));

    if (make) {
      fetchModels(fetch, dispatch, make);
    }
  },

  model: modelId => (dispatch, getState, { fetch }) => {
    fetch(API.CMM.modelById(modelId)).then(r => r.json()).then(response => {
      if (response) {
        dispatch(set.model(response));
      }
    });
  },

  isOfficial: isOfficial => dispatch => {
    dispatch(set.isOfficial(isOfficial));
  },

  q: q => dispatch => {
    dispatch(set.q(q));
  },

  testDrive: testDrive => dispatch => {
    dispatch(set.testDrive(testDrive));
  },

  tags: tags => dispatch => {
    dispatch(set.tags(tags));
  },

  isNew: isNew => dispatch => {
    const result = parseInt(isNew, 10);
    dispatch(set.isNew(isNaN(result) ? null : result));
  },

  tradeIn: tradeIn => dispatch => {
    dispatch(set.tradeIn(tradeIn));
  },

  credit: credit => dispatch => {
    dispatch(set.credit(credit));
  },

  insurance: insurance => dispatch => {
    dispatch(set.insurance(insurance));
  },

  accessories: accessories => dispatch => {
    dispatch(set.accessories(accessories));
  },
};

//
// Spares
//

export const sparesSetThunks = {
  make: make => (dispatch, getState, { fetch }) => {
    dispatch(set.make(make));
    dispatch(set.model(null));
    dispatch(setData.models(null));
    dispatch(sparesSetThunks.count());

    if (make) {
      fetchModels(fetch, dispatch, make);
    }
  },

  model: model => dispatch => {
    dispatch(set.model(model));
    dispatch(sparesSetThunks.count());
  },

  count: transferredState => (dispatch, getState, { fetch }) => {
    const { searchForm: { selected: formState, counts }, globals } = getState();
    const location = (globals && globals.location) || null;

    const searchQuery = {
      ...(transferredState || formState),
      location: formState.dealer ? null : location,
    };
    const { currency, ...usefulParams } = searchQuery;
    const query = snakeCaseKeys(generateSearchQuery(usefulParams));
    const key = queryString.stringify(query);
    const specialKey = `spare-${key}`;
    const currentCount = counts && counts[specialKey];

    if (currentCount !== undefined && !isNaN(currentCount)) {
      dispatch(setData.count(currentCount));
    } else {
      fetch(`${API.SPARE_PARTS.COUNT}?${key}`).then(r => r.json()).then(response => {
        dispatch(setCounts({ [specialKey]: response.count }));
        dispatch(setData.count(response.count));
      });
    }
  },

  yearTo: yearTo => dispatch => {
    dispatch(set.yearTo(yearTo));
    dispatch(sparesSetThunks.count());
  },

  yearFrom: yearFrom => dispatch => {
    dispatch(set.yearFrom(yearFrom));
    dispatch(sparesSetThunks.count());
  },

  rubric: section => (dispatch, getState, { fetch }) => {
    dispatch(set.rubric(section));
    dispatch(setData.sparetypes(null));
    dispatch(set.sparetype(null));
    dispatch(sparesSetThunks.count());

    if (section) {
      fetch(API.CMM.sparetypesBySection(section.id)).then(r => r.json()).then(response => {
        if (response) {
          dispatch(setData.sparetypes(response));
        }
      });
    }
  },

  sparetype: sparetype => dispatch => {
    dispatch(set.sparetype(sparetype));
    dispatch(sparesSetThunks.count());
  },

  query: q => dispatch => {
    dispatch(set.query(q));
    dispatch(sparesSetThunks.count());
  },

  isUsed: isUsed => dispatch => {
    dispatch(set.isUsed(isUsed));
    dispatch(sparesSetThunks.count());
  },

  isNew: isNew => dispatch => {
    dispatch(set.isNew(isNew));
    dispatch(sparesSetThunks.count());
  },

  isOriginal: isOriginal => dispatch => {
    dispatch(set.isOriginal(isOriginal));
    dispatch(sparesSetThunks.count());
  },

  isAnalogue: isAnalogue => dispatch => {
    dispatch(set.isAnalogue(isAnalogue));
    dispatch(sparesSetThunks.count());
  },

  inStock: inStock => dispatch => {
    dispatch(set.inStock(inStock));
    dispatch(sparesSetThunks.count());
  },

  isUnderTheOrder: isUnderTheOrder => dispatch => {
    dispatch(set.isUnderTheOrder(isUnderTheOrder));
    dispatch(sparesSetThunks.count());
  },

  clearSelected: () => dispatch => {
    dispatch(clearSelected());
    dispatch(sparesSetThunks.count());
  },
};

export const sparesFormFillThunks = {
  make: makeId => (dispatch, getState, { fetch }) => {
    const collection = getState().searchForm.vocabulary.makes;
    const make = findItemInCollection(collection, parseInt(makeId, 10));
    dispatch(set.make(make));

    if (make) {
      fetchModels(fetch, dispatch, make);
    }
  },

  model: modelId => (dispatch, getState, { fetch }) => {
    fetch(API.CMM.modelById(modelId)).then(r => r.json()).then(response => {
      if (response) {
        dispatch(set.model(response));
      }
    });
  },

  yearTo: yearTo => dispatch => {
    dispatch(set.yearTo(yearTo));
  },

  yearFrom: yearFrom => dispatch => {
    dispatch(set.yearFrom(yearFrom));
  },

  // isNew group

  isUsed: isUsed => dispatch => {
    if (isUsed) {
      dispatch(set.isNew(null));
    }
    dispatch(set.isUsed(parseFloat(isUsed)));
  },

  isNew: isNew => dispatch => {
    if (isNew) {
      dispatch(set.isUsed(null));
    }
    dispatch(set.isNew(isNew === null ? null : parseInt(isNew, 10)));
  },

  // /

  // isOriginal group

  isOriginal: isOriginal => dispatch => {
    if (isOriginal) {
      dispatch(set.isAnalogue(null));
    }
    dispatch(set.isOriginal(parseFloat(isOriginal)));
  },

  isAnalogue: isAnalogue => dispatch => {
    if (isAnalogue) {
      dispatch(set.isOriginal(null));
    }
    dispatch(set.isAnalogue(parseFloat(isAnalogue)));
  },

  // /

  // inStock group

  inStock: inStock => dispatch => {
    if (inStock) {
      dispatch(set.isUnderTheOrder(null));
    }
    dispatch(set.inStock(parseFloat(inStock)));
  },

  isUnderTheOrder: isUnderTheOrder => dispatch => {
    if (isUnderTheOrder) {
      dispatch(set.inStock(null));
    }
    dispatch(set.isUnderTheOrder(parseFloat(isUnderTheOrder)));
  },

  // /

  rubric: sectionId => (dispatch, getState, { fetch }) => {
    const collection = getState().searchForm.vocabulary.rubrics;
    const rubric = findItemInCollection(collection, parseFloat(sectionId));

    dispatch(set.sparetype(null));
    dispatch(setData.sparetypes(null));
    dispatch(set.rubric(rubric));

    if (sectionId) {
      fetch(API.CMM.sparetypesBySection(sectionId)).then(r => r.json()).then(response => {
        if (response) {
          dispatch(setData.sparetypes(response));
        }
      });
    }
  },

  sparetype: subsectionId => (dispatch, getState, { fetch }) => {
    fetch(API.CMM.sparetypesById(subsectionId)).then(r => r.json()).then(response => {
      if (response && response.length > 0) {
        const subsection = response[0];
        dispatch(set.sparetype(subsection));
      }
    });
  },

  query: q => dispatch => {
    dispatch(set.query(q));
  },

  firm: firm => dispatch => {
    dispatch(set.firm(firm));
  },
};

//
// Other
//

export const otherTransportSetThunks = {
  make: (make, transportID) => (dispatch, getState, { fetch }) => {
    dispatch(set.make(make));
    dispatch(set.model(null));
    dispatch(setData.models(null));

    if (make) {
      fetchModels(fetch, dispatch, make, { id: transportID });
    }
  },

  model: model => (dispatch, getState, { fetch }) => {
    dispatch(set.model(model));
    dispatch(set.series(null));
    dispatch(setData.series(null));

    if (model) {
      fetch(API.CMM.seriesByModel(model.id)).then(r => r.json()).then(series => {
        dispatch(setData.series(series));
      });
    }
  },

  series: series => (dispatch, getState, { fetch }) => {
    dispatch(set.series(series));
    dispatch(set.year(null));
    dispatch(setData.modifications(null));
    dispatch(setData.years(null));

    if (series) {
      fetch(API.CMM.modificationsBySeries(series.id)).then(r => r.json()).then(modifications => {
        const modificationsYearsUniq = new Set(
          modifications
            .map(modification =>
              getRange(parseInt(modification.year_begin, 10), parseInt(modification.year_end, 10)),
            )
            .reduce((result, generationYears) => [...result, ...generationYears], []),
        ).toJSON();

        dispatch(setData.modifications(modifications));
        dispatch(setData.years(modificationsYearsUniq));
      });
    }
  },
};
//
// moto
//

export const setTransportResults = id => (dispatch, getState, { fetch }) =>
  fetch(API.ADVERT.ALL(`?transport=${id}`)).then(r => r.json()).then(r => {
    dispatch(setResults(r));
    dispatch(
      setDataLayer(stateDataLayer.manipulation({ data: r.results, pagetype: 'searchresults' })),
    );
  });

export const setTransportType = transport => (dispatch, getState) => {
  const currentTransport = getState().searchForm.selected.transport;
  if (!currentTransport || currentTransport.id !== transport.id) dispatch(clearSelected());
  dispatch(set.transport(transport));
  dispatch(transportSetThunks.count());
};



// WEBPACK FOOTER //
// ./src/shared/actions/searchForm.js